import {
  Add01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Delete01Icon,
  Edit01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { ASSET_PERMISSIONS } from 'shared';
import { PermissionButton } from '@/components/shared/PermissionButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AssetCombobox } from '@/components/ui/asset-combobox';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  useAssetStatusEvents,
  useCreateAssetStatusEvent,
  useDeleteAssetStatusEvent,
  useUpdateAssetStatusEvent,
} from '@/hooks/use-asset-status-events';
import { apiGet } from '@/lib/api-client';
import type {
  CreateAssetStatusEventInput,
  UpdateAssetStatusEventInput,
} from '@/lib/asset-status-client';
import { paginationConfig } from '@/lib/config/pagination';

export const Route = createFileRoute('/_authenticated/asset-status-events')({
  component: AssetStatusEventsPage,
});

const SOURCE_OPTIONS = [
  { value: 'sensor', label: 'Sensor' },
  { value: 'manual', label: 'Manual' },
  { value: 'system', label: 'System' },
];

interface Status {
  id: string;
  name: string;
  color: string;
}

function AssetStatusEventForm({
  event,
  onSubmit,
  statusesData,
}: {
  event?: any;
  onSubmit: (data: CreateAssetStatusEventInput | UpdateAssetStatusEventInput) => Promise<void>;
  statusesData: Status[];
}) {
  const form = useForm({
    defaultValues: {
      assetId: event?.assetId || '',
      statusId: event?.statusId || '',
      source: (event?.source as 'sensor' | 'manual' | 'system') || 'manual',
      notes: event?.notes || '',
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  if (statusesData.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-destructive">
          No asset statuses available. Please create an asset status first.
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="space-y-4"
    >
      <form.Field
        name="assetId"
        validators={{ onChange: ({ value }) => (!value ? 'Asset is required' : undefined) }}
      >
        {(field) => (
          <div className="space-y-2">
            <Label>Asset</Label>
            <AssetCombobox
              selectedId={field.state.value || null}
              onSelect={(value) => field.handleChange(value || '')}
              placeholder="Select an asset..."
            />
            {field.state.meta.errors && (
              <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
            )}
          </div>
        )}
      </form.Field>

      <form.Field
        name="statusId"
        validators={{ onChange: ({ value }) => (!value ? 'Status is required' : undefined) }}
      >
        {(field) => {
          const selectedStatus = statusesData.find((s) => s.id === field.state.value);
          return (
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={field.state.value || undefined}
                onValueChange={(value) => field.handleChange(value || '')}
              >
                <SelectTrigger className="w-full">
                  {selectedStatus ? (
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: selectedStatus.color }}
                      />
                      <span>{selectedStatus.name}</span>
                    </div>
                  ) : (
                    <SelectValue />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {statusesData.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full border"
                          style={{ backgroundColor: s.color }}
                        />
                        <span>{s.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {field.state.meta.errors && (
                <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
              )}
            </div>
          );
        }}
      </form.Field>

      <form.Field
        name="source"
        validators={{ onChange: ({ value }) => (!value ? 'Source is required' : undefined) }}
      >
        {(field) => (
          <div className="space-y-2">
            <Label>Source</Label>
            <Select
              value={field.state.value}
              onValueChange={(value) => field.handleChange(value as 'sensor' | 'manual' | 'system')}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.state.meta.errors && (
              <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
            )}
          </div>
        )}
      </form.Field>

      <form.Field name="notes">
        {(field) => (
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              id={field.name}
              value={field.state.value || ''}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Optional notes about this status change..."
            />
          </div>
        )}
      </form.Field>

      <div className="flex gap-2 justify-end">
        <Button type="submit" disabled={form.state.isSubmitting}>
          {form.state.isSubmitting ? 'Saving...' : event ? 'Update Event' : 'Create Event'}
        </Button>
      </div>
    </form>
  );
}

function AssetStatusEventsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(paginationConfig.defaultPageSize);
  const [search, setSearch] = useState('');
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [deletingEvent, setDeletingEvent] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);

  // Reset to page 1 when page size changes
  useEffect(() => {
    setPage(1);
  }, []);

  const { data, isLoading, error } = useAssetStatusEvents({ page, limit: pageSize, search });

  // Pre-fetch statuses
  const [statusesData, setStatusesData] = useState<Status[]>([]);

  const createEvent = useCreateAssetStatusEvent();
  const updateEvent = useUpdateAssetStatusEvent();
  const deleteEvent = useDeleteAssetStatusEvent();

  // Fetch statuses when form opens
  useEffect(() => {
    if (showForm) {
      apiGet<{ data: Status[] }>('/asset-statuses?limit=100').then((statusesResp) => {
        setStatusesData(statusesResp.data || []);
      });
    }
  }, [showForm]);

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'assetName',
      header: 'Asset',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.assetName || 'N/A'}</div>
          <div className="text-xs text-muted-foreground">{row.original.assetCode || ''}</div>
        </div>
      ),
    },
    {
      accessorKey: 'statusName',
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.statusColor && (
            <div
              className="w-4 h-4 rounded-full border"
              style={{ backgroundColor: row.original.statusColor }}
            />
          )}
          <span>{row.original.statusName || 'N/A'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: ({ row }) => <span className="capitalize">{row.original.source}</span>,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => <span>{new Date(row.original.createdAt).toLocaleString()}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditingEvent(row.original);
              setShowForm(true);
            }}
          >
            <HugeiconsIcon icon={Edit01Icon} className="size-4" />
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setDeletingEvent(row.original)}>
            <HugeiconsIcon icon={Delete01Icon} className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: data?.totalPages || 0,
    state: { pagination: { pageIndex: page - 1, pageSize: pageSize } },
    manualPagination: true,
  });

  const closeForm = () => {
    setShowForm(false);
    setEditingEvent(null);
  };
  const handleCreate = async (input: CreateAssetStatusEventInput | UpdateAssetStatusEventInput) => {
    await createEvent.mutateAsync(input as CreateAssetStatusEventInput);
    closeForm();
  };
  const handleUpdate = async (input: CreateAssetStatusEventInput | UpdateAssetStatusEventInput) => {
    if (!editingEvent) return;
    await updateEvent.mutateAsync({
      id: editingEvent.id,
      input: input as UpdateAssetStatusEventInput,
    });
    closeForm();
  };
  const handleDelete = async () => {
    if (!deletingEvent) return;
    await deleteEvent.mutateAsync(deletingEvent.id);
    setDeletingEvent(null);
  };

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-destructive">Error loading asset status events</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Asset Status Events</h1>
          <p className="text-muted-foreground">History of asset status changes</p>
        </div>
        <Button
          onClick={() => {
            setEditingEvent(null);
            setShowForm(true);
          }}
        >
          <HugeiconsIcon icon={Add01Icon} className="size-4 mr-2" /> Add Event
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={(open) => (open ? setShowForm(true) : closeForm())}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Edit Asset Status Event' : 'Create Asset Status Event'}
            </DialogTitle>
          </DialogHeader>
          <AssetStatusEventForm
            event={editingEvent || undefined}
            onSubmit={editingEvent ? handleUpdate : handleCreate}
            statusesData={statusesData}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingEvent} onOpenChange={(open) => !open && setDeletingEvent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset Status Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <PermissionButton asChild permission={ASSET_PERMISSIONS.DELETE}>
              <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
            </PermissionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader>
          <CardTitle>Asset Status Events List</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Search by asset name, code, or status..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="max-w-sm"
          />
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((h) => (
                      <TableHead key={h.id} className="border-b px-4 py-2 text-left font-medium">
                        {h.isPlaceholder
                          ? null
                          : flexRender(h.column.columnDef.header, h.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      No asset status events found
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="px-4 py-2">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {data?.totalPages && data.totalPages > 1 && (
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Page {page} of {data.totalPages} ({data.totalItems || 0} total)
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) =>
                    setPageSize(
                      value === '0'
                        ? paginationConfig.availablePageSizes.find((s) => s === 0) ||
                            data.totalItems ||
                            pageSize
                        : Number(value),
                    )
                  }
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paginationConfig.availablePageSizes.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size === 0 ? 'All' : size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data && page >= data.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                  <HugeiconsIcon icon={ArrowRight01Icon} className="size-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
